// src/layouts/reports/ReportViewer.jsx
// -----------------------------------------------------------------------------
// Visualisation d’un rapport Budget vs Actuals (lecture seule).
// Données combinées :
//   - Données QuickBooks (Actual/Budget)
//   - Lignes custom (Projection/Modification, + Pending et Status)
// Colonnes (dans l’ordre) :
//   Account → Total Actual → Projection(/Pending) → Actual + Projection
//   → Total Budget → Modification(/Pending) → Budget Update → Remaining → Total
//
// ⚠️ FIX demandé : Remaining = BudgetUpdate - (Actual + Projection)
// -----------------------------------------------------------------------------

import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Snackbar,
  Alert,
  CircularProgress,
  Chip,
  Stack,
} from "@mui/material";

import api from "api/axios";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";
import DataTable from "examples/Tables/DataTable";

// Mapping statut → couleur MUI
const STATUS_COLORS = {
  NONE: "default",
  PENDING_SUP: "warning",
  PENDING_GA: "warning",
  PENDING_ED: "warning",
  APPROVED: "success",
  REJECTED: "error",
};

// Libellés d’aide (info-bulle)
const STATUS_LABELS = {
  NONE: "Appliqué",
  PENDING_SUP: "En attente (Supervisor)",
  PENDING_GA: "En attente (Grants Admin)",
  PENDING_ED: "En attente (Executive Director)",
  APPROVED: "Approuvé",
  REJECTED: "Rejeté",
};

// Affichage numérique uniforme (FR)
function formatMoney(n, locale = "fr-FR", options = { maximumFractionDigits: 2 }) {
  const value = Number(n || 0);
  return value.toLocaleString(locale, options);
}

// Pastille valeur + statut (outlined = appliquée, filled = pending)
function valueChip(value, status = "NONE", { outlined = true, title } = {}) {
  return (
    <Chip
      size="small"
      label={formatMoney(value)}
      color={STATUS_COLORS[status] || "default"}
      variant={outlined ? "outlined" : "filled"}
      title={title || STATUS_LABELS[status] || ""}
    />
  );
}

const ReportViewer = () => {
  const { reportId } = useParams();

  const [reportMeta, setReportMeta] = useState(null);
  const [reportData, setReportData] = useState([]);
  // Map: account_name -> { projection, modification, *_pending, *_status }
  const [rowMap, setRowMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "error" });

  useEffect(() => {
    const fetchReportData = async () => {
      setLoading(true);
      try {
        // 1) Métadonnées du rapport
        const metaRes = await api.get(`/quickbooks/custom-reports/${reportId}/`);
        setReportMeta(metaRes.data);

        // 2) Paramètres de filtre (année fiscale)
        const fiscalYear = metaRes.data?.filters?.fiscal_year || "2025";
        const queryParams = new URLSearchParams({
          fiscal_year: fiscalYear,
          report_id: reportId,
        });

        // 3) Données QuickBooks + lignes custom (projections / modifications)
        const [dataRes, rowsRes] = await Promise.all([
          api.get(`/quickbooks/reports/budget-vs-actuals/?${queryParams}`),
          api.get(`/quickbooks/custom-reports/${reportId}/rows/`),
        ]);

        const qbRows = dataRes?.data?.data || [];
        setReportData(qbRows);

        // 4) Construction du rowMap (accès O(1) par nom de compte)
        const map = {};
        (rowsRes.data || []).forEach((r) => {
          const name = r.account_name || r.account || r.AccountName;
          if (!name) return;
          map[name] = {
            projection: r.projection ?? 0,
            modification: r.modification ?? 0,
            projection_pending: r.projection_pending ?? null,
            modification_pending: r.modification_pending ?? null,
            projection_status: r.projection_status || "NONE",
            modification_status: r.modification_status || "NONE",
          };
        });
        setRowMap(map);
      } catch (error) {
        console.error("Erreur lors du chargement :", error);
        setSnackbar({ open: true, message: "Erreur lors du chargement", severity: "error" });
      } finally {
        setLoading(false);
      }
    };

    fetchReportData();
  }, [reportId]);

  // Colonnes (entêtes)
  const columns = [
    { Header: "Account", accessor: "account", align: "left" },
    { Header: "Total Actual", accessor: "total_actual", align: "right" },
    { Header: "Projection / Pending", accessor: "projection_col", align: "left" },
    { Header: "Actual + Projection", accessor: "actual_projection", align: "right" },
    { Header: "Total Budget", accessor: "total_budget", align: "right" },
    { Header: "Modification / Pending", accessor: "modification_col", align: "left" },
    { Header: "Budget Update", accessor: "budget_update", align: "right" },
    { Header: "Remaining", accessor: "total_remaining", align: "right" },
    // "Total" = pourcentage consommé (on garde la sémantique existante)
    { Header: "Total", accessor: "total_percent", align: "right" },
  ];

  // Lignes dérivées
  const rows = [...reportData]
    .sort((a, b) => a.AccountName.localeCompare(b.AccountName))
    .map((row) => {
      const acct = row.AccountName;
      const actual = Number(row.TotalActual || 0);
      const budget = Number(row.TotalBudget || 0);

      const meta = rowMap[acct] || {};
      const projection = Number(meta.projection || 0);
      const modification = Number(meta.modification || 0);
      const pPending = meta.projection_pending ?? null;
      const mPending = meta.modification_pending ?? null;

      // Calculs dérivés
      const actualProjection = actual + projection;      // A+P
      const budgetUpdate = budget + modification;        // B+Δ
      const remaining = budgetUpdate - actualProjection; // ✅ FIX: Remaining = (B+Δ) - (A+P)

      return {
        account: acct,

        total_actual: formatMoney(actual),

        projection_col: (
          <Stack direction="row" spacing={1} alignItems="center">
            {valueChip(projection, meta.projection_status, {
              outlined: true,
              title: `Projection — ${STATUS_LABELS[meta.projection_status] || ""}`,
            })}
            {pPending !== null &&
              valueChip(Number(pPending), meta.projection_status, {
                outlined: false,
                title: "Projection — En attente",
              })}
          </Stack>
        ),

        actual_projection: formatMoney(actualProjection),

        total_budget: formatMoney(budget),

        modification_col: (
          <Stack direction="row" spacing={1} alignItems="center">
            {valueChip(modification, meta.modification_status, {
              outlined: true,
              title: `Modification — ${STATUS_LABELS[meta.modification_status] || ""}`,
            })}
            {mPending !== null &&
              valueChip(Number(mPending), meta.modification_status, {
                outlined: false,
                title: "Modification — En attente",
              })}
          </Stack>
        ),

        budget_update: formatMoney(budgetUpdate),

        total_remaining: formatMoney(remaining),

        // % consommé (on garde la définition actuelle = Actual / BudgetUpdate)
        total_percent:
          budgetUpdate > 0 ? `${((actual / budgetUpdate) * 100).toFixed(1)}%` : "0%",
      };
    });

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Box sx={{ marginBottom: "24px" }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h4" gutterBottom>
                {reportMeta?.name || "Budget vs Actuals Report"}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                {reportMeta?.description}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" mt={4}>
            <CircularProgress />
          </Box>
        ) : (
          <DataTable
            table={{ columns, rows }}
            isLoading={loading}
            noEndBorder
            entriesPerPage={false}
            pagination={{ enabled: false }}
            showTotalEntries={false}
            stickyHeader
            stickyColumn
            renderAllRows
          />
        )}
      </MDBox>
      <Footer />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

export default ReportViewer;
